import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import axios from "src/utils/axios";
import GetUrlApi from "src/utils/UrlApi";

import { getDeviceId } from "src/utils/deviceId";
import * as recorder from "src/utils/debug/debugRecorder";
import { nextBackoffMs, splitBatch, MAX_BATCH_ITEMS } from "src/utils/debug/monitorPolicy";

// Fase 5 · Flush cada 5 s (antes 30 s): el panel de depuración en vivo
// necesita ver los clicks casi al momento. Sin nada nuevo, no hay POST.
const FLUSH_INTERVAL_MS = 5 * 1000;

// Filosofía: el switch global ON significa "estoy diagnosticando AHORA,
// quiero verlo TODO". Persistimos cada petición que pase por el slice
// incluyendo bodies (request + response) truncados a 1000 chars.
// Las defensas contra saturación son:
//  - Watchdog en backend (1M filas/restaurante → 429).
//  - Rate limit backend por dispositivo → 429 = reintento con espera.
//  - Retención automática (7d para ApiRequestLogs).
//  - Truncado de bodies a 1000 chars en cliente + backend.
const BODY_MAX_CHARS = 1000;

// Mapa source → texto legible. El admin del panel lo lee así: ve
// "TPV camarero · Juan García" en lugar de "tpv-camarero" + UUID feo.
function sourceLabel(src) {
  switch (src) {
    case "tpv-camarero":
    case "tpv-carta": return "TPV camarero";
    case "tpv-grid": return "TPV mesas";
    case "manage-admin":
    case "admin": return "Admin";
    case "caja": return "Caja";
    case "kds": return "KDS";
    case "kitchen": return "Cocina";
    case "carta": return "Carta cliente";
    default: return src || "App";
  }
}

function buildDeviceLabel(source, userName, employeeName) {
  const base = sourceLabel(source);
  const sub = employeeName || userName;
  return sub ? `${base} · ${sub}` : base;
}

// Truncamos bodies grandes en cliente para no enviar megas. El backend
// vuelve a truncar (defense in depth) — máx 4000 chars.
function trunc(value, max = 4000) {
  if (value == null) return null;
  let s = typeof value === "string" ? value : safeJson(value);
  if (s == null) return null;
  return s.length <= max ? s : s.substring(0, max);
}
function safeJson(v) {
  try { return JSON.stringify(v); } catch { return null; }
}

// Solo una instancia activa aunque varios layouts monten el hook (anidados
// o durante el cambio de layout). La que llega segunda no hace nada.
let activeOwner = null;

/**
 * Persiste en backend los requests/errors capturados por el slice
 * `errors` (interceptor de axios) + los eventos del grabador de depuración
 * (clicks, eventos SignalR, reconexiones…). SOLO si el monitor está ON.
 * Sin esa activación el hook es no-op total.
 *
 * Estrategia:
 *  - HTTP: `cursor` por tiempo con el último item ya enviado.
 *  - Grabador: cola propia; se retira lo enviado solo tras 200 OK.
 *  - 429 (rate limit / watchdog): NO se avanza nada; se reintenta tras una
 *    espera creciente (10 s → 2 min). Antes se avanzaba el cursor y se
 *    perdían filas.
 *  - Al ocultar/cerrar la página: envío con `keepalive`.
 */
export default function useApiMonitorPersist({ source = "admin" } = {}) {
  const isMonitoring = useSelector((s) => s.errors?.isMonitoring);
  const requests = useSelector((s) => s.errors?.requests || []);
  const errors = useSelector((s) => s.errors?.errors || []);

  const ownerRef = useRef({});
  // Si la instancia dueña se desmontó, la siguiente que pregunte se queda.
  const isOwner = () => {
    if (!activeOwner) activeOwner = ownerRef.current;
    return activeOwner === ownerRef.current;
  };

  // Para componer el deviceLabel legible. Si hay empleado fichado en
  // TPV usamos ese nombre — si no, el del titular del restaurante.
  const userName = useSelector(
    (s) => s.authJwt?.user?.nombreEmpresa || s.authJwt?.user?.name || null
  );
  const employeeName = useSelector(
    (s) => s.employee?.currentActiveEmployee?.name
        || s.employee?.currentActiveEmployee?.completeName
        || null
  );
  const refSource = useRef(source);
  const refDeviceLabel = useRef(buildDeviceLabel(source, userName, employeeName));
  useEffect(() => {
    refSource.current = source;
    refDeviceLabel.current = buildDeviceLabel(source, userName, employeeName);
  }, [source, userName, employeeName]);

  // Cursor en localStorage para sobrevivir a remontajes y recargas.
  const CURSOR_KEY = `tmo-monitor-cursor-${getDeviceId()}`;
  const readCursor = () => {
    try { return Number(localStorage.getItem(CURSOR_KEY) || 0); } catch { return 0; }
  };
  const writeCursor = (v) => {
    try { localStorage.setItem(CURSOR_KEY, String(v)); } catch { /* ignore */ }
  };
  const lastSentAt = useRef(readCursor());
  const backoffMs = useRef(0);
  const retryAfter = useRef(0);

  const refRequests = useRef(requests);
  const refErrors = useRef(errors);
  const refIsMonitoring = useRef(isMonitoring);
  useEffect(() => { refRequests.current = requests; }, [requests]);
  useEffect(() => { refErrors.current = errors; }, [errors]);
  useEffect(() => {
    refIsMonitoring.current = isMonitoring;
    recorder.refreshRecordingFlag();
  }, [isMonitoring]);

  // Reclamar la propiedad al montar; soltarla al desmontar.
  useEffect(() => {
    const me = ownerRef.current;
    if (!activeOwner) activeOwner = me;
    recorder.attachStorage(getDeviceId());
    return () => {
      if (activeOwner === me) activeOwner = null;
    };
  }, []);

  // Cuando se desactiva, flush final + reset cursor para arranque limpio.
  const prevMonitoring = useRef(isMonitoring);
  useEffect(() => {
    if (!isOwner()) { prevMonitoring.current = isMonitoring; return; }
    if (prevMonitoring.current === true && isMonitoring === false) {
      flush({ force: true }).finally(() => {
        lastSentAt.current = 0;
        writeCursor(0);
        recorder.clear();
      });
    }
    if (prevMonitoring.current === false && isMonitoring === true) {
      // Arranque: cursor = AHORA. Solo lo nuevo cuenta como diagnóstico.
      const now = Date.now();
      lastSentAt.current = now;
      writeCursor(now);
      backoffMs.current = 0;
      retryAfter.current = 0;
    }
    prevMonitoring.current = isMonitoring;
  }, [isMonitoring]); // eslint-disable-line

  const buildHttpItems = () => {
    const reqs = refRequests.current;
    const errs = refErrors.current;
    const deviceId = getDeviceId();
    const deviceLabel = refDeviceLabel.current;
    const src = refSource.current;
    const items = [];

    const ts = (v) => v?.startedAt ? new Date(v.startedAt).getTime() : (v?.endedAt ? new Date(v.endedAt).getTime() : Date.now());
    const debugFields = (v) => ({
      kind: "http",
      actionId: v?.debug?.actionId || null,
      actionName: v?.debug?.actionName || null,
      screen: v?.debug?.screen || null,
    });

    for (const r of reqs) {
      const t = ts(r);
      if (t <= lastSentAt.current) continue;
      items.push({
        deviceId,
        deviceLabel,
        source: src,
        method: r.method,
        url: r.url,
        status: r.status,
        durationMs: Math.round(r.duration ?? 0),
        requestBody: trunc(r.requestBody, BODY_MAX_CHARS),
        responseBody: trunc(r.responseBody, BODY_MAX_CHARS),
        errorMessage: null,
        traceId: r.traceId || null,
        isError: false,
        startedAt: r.startedAt ? new Date(r.startedAt).toISOString() : new Date().toISOString(),
        ...debugFields(r),
      });
    }
    for (const e of errs) {
      const t = ts(e);
      if (t <= lastSentAt.current) continue;
      items.push({
        deviceId,
        deviceLabel,
        source: src,
        method: e.method,
        url: e.url,
        status: e.status,
        durationMs: Math.round(e.duration ?? 0),
        requestBody: trunc(e.requestBody, BODY_MAX_CHARS),
        responseBody: trunc(e.responseBody, BODY_MAX_CHARS),
        errorMessage: trunc(e.message || (typeof e.customMessage === "string" ? e.customMessage : safeJson(e.customMessage)), 2000),
        traceId: e.traceId || null,
        isError: true,
        startedAt: e.startedAt ? new Date(e.startedAt).toISOString() : new Date().toISOString(),
        ...debugFields(e),
      });
    }
    items.sort((a, b) => new Date(a.startedAt) - new Date(b.startedAt));
    return items;
  };

  const buildBatch = () => {
    const ctx = { deviceId: getDeviceId(), deviceLabel: refDeviceLabel.current, source: refSource.current };
    const { http, rec } = splitBatch(buildHttpItems(), recorder.peek(MAX_BATCH_ITEMS), MAX_BATCH_ITEMS);
    const recItems = rec.map((ev) => recorder.toBatchItem(ev, ctx));
    return { http, recCount: rec.length, items: http.concat(recItems) };
  };

  const commitBatch = (http, recCount) => {
    if (http.length) {
      const newCursor = http.reduce(
        (acc, it) => Math.max(acc, new Date(it.startedAt).getTime()),
        lastSentAt.current,
      );
      lastSentAt.current = newCursor;
      writeCursor(newCursor);
    }
    recorder.commit(recCount);
  };

  // Flag para evitar flush concurrente (intervalo + unmount).
  const flushing = useRef(false);

  const flush = async ({ force = false } = {}) => {
    if (!isOwner()) return;
    if (!refIsMonitoring.current && !force) return;
    if (flushing.current) return;
    if (!force && Date.now() < retryAfter.current) return;
    flushing.current = true;
    try {
      const { http, recCount, items } = buildBatch();
      if (items.length === 0) return;
      try {
        await axios.post("/api-monitor/batch", { items });
        // SOLO tras 200 OK se avanza.
        commitBatch(http, recCount);
        backoffMs.current = 0;
        retryAfter.current = 0;
      } catch (err) {
        const status = err?.response?.status || err?.status;
        if (status === 429) {
          // Rate limit / watchdog: se reintenta lo mismo más tarde, con
          // espera creciente. Nada se da por enviado.
          backoffMs.current = nextBackoffMs(backoffMs.current);
          retryAfter.current = Date.now() + backoffMs.current;
        }
        // Otros errores: tampoco se avanza → siguiente flush.
      }
    } finally {
      flushing.current = false;
    }
  };

  // Envío al ocultar/cerrar la página: fetch con keepalive (axios no lo
  // soporta). Se da por enviado — si llega a repetirse, el panel lo tolera
  // mejor que perder el tramo final antes de un cierre o una recarga.
  const flushOnHide = () => {
    if (!isOwner() || !refIsMonitoring.current) return;
    try {
      const { http, recCount, items } = buildBatch();
      if (!items.length) { recorder.persistNow(); return; }
      const headers = { "Content-Type": "application/json" };
      const auth = axios.defaults?.headers?.common?.Authorization;
      if (auth) headers.Authorization = auth;
      const sid = axios.defaults?.headers?.common?.SessionIdentifier;
      if (sid) headers.SessionIdentifier = sid;
      const body = JSON.stringify({ items });
      if (body.length > 60000) { recorder.persistNow(); return; } // límite keepalive ~64 KB
      fetch(`${GetUrlApi()}/api/api-monitor/batch`, { method: "POST", headers, body, keepalive: true }).catch(() => {});
      commitBatch(http, recCount);
      recorder.persistNow();
    } catch { /* nunca romper el cierre */ }
  };

  // Ref a las funciones para que setInterval/listeners usen la versión actual.
  const refFlushFn = useRef(flush);
  const refHideFn = useRef(flushOnHide);
  useEffect(() => { refFlushFn.current = flush; refHideFn.current = flushOnHide; });

  useEffect(() => {
    const id = setInterval(() => { refFlushFn.current && refFlushFn.current(); }, FLUSH_INTERVAL_MS);
    const onHide = () => refHideFn.current && refHideFn.current();
    const onVisibility = () => { if (document.visibilityState === "hidden") onHide(); };
    window.addEventListener("pagehide", onHide);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      clearInterval(id);
      window.removeEventListener("pagehide", onHide);
      document.removeEventListener("visibilitychange", onVisibility);
      refFlushFn.current && refFlushFn.current();
    };
  }, []); // mount-once
}
